// src/pages/ClientsPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Users,
  Search,
  Filter,
  Mail,
  Phone,
  MapPin,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  TrendingUp,
  AlertCircle,
  Calendar,
  X,
} from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

const ClientsPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);

  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    newThisMonth: 0,
    totalProperties: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // View modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [activeClient, setActiveClient] = useState(null);

  // Delete state
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  // ✅ Helpers: read auth token & role
  const getAuthToken = () =>
    localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  const getAuthRole = () =>
    localStorage.getItem('authRole') || sessionStorage.getItem('authRole');

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
      inactive: { bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
    };

    const { bg, text, icon: Icon } = config[status] || config.active;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold ${bg} ${text}`}
      >
        <Icon className="w-3 h-3" />
        <span className="hidden xs:inline">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
        <span className="xs:hidden">{status.charAt(0).toUpperCase()}</span>
      </span>
    );
  };

  const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const params = new URLSearchParams();
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const url = `https://patrol-visit.patroll-management.nissiindia.com/api/clients${
        params.toString() ? `?${params.toString()}` : ''
      }`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please log in again.');
          logoutAndRedirect();
          throw new Error(data.message || 'Invalid or expired session');
        }
        throw new Error(data.message || 'Failed to load clients');
      }

      setClients(data.clients || []);
      setStats({
        totalClients: data.stats?.totalClients || 0,
        activeClients: data.stats?.activeClients || 0,
        newThisMonth: data.stats?.newThisMonth || 0,
        totalProperties: data.stats?.totalProperties || 0,
      });
      setTotalCount(data.totalCount || 0);
      setFilteredCount(data.filteredCount || data.clients?.length || 0);
      setSelectedClients([]);
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Something went wrong while loading clients';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter]);

  const statsCards = [
    {
      title: 'Total Clients',
      value: stats.totalClients,
      change: '+0%',
      icon: Users,
    },
    {
      title: 'Active Clients',
      value: stats.activeClients,
      change: '+0%',
      icon: CheckCircle,
    },
    {
      title: 'New This Month',
      value: stats.newThisMonth,
      change: '+0%',
      icon: TrendingUp,
    },
    {
      title: 'Total Properties',
      value: stats.totalProperties,
      change: '+0%',
      icon: Building2,
    },
  ];

  // Handlers
  const handleViewClient = (client) => {
    setActiveClient(client);
    setViewModalOpen(true);
  };

  const handleEditClient = (client) => {
    // Redirect to AddClientForm with id as query param
    navigate(`/clients/add?id=${client.id}`);
  };

  const handleDeleteClient = async (client) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete client "${client.name}"?`
    );
    if (!confirmed) return;

    try {
      const token = getAuthToken();
      const role = getAuthRole();

      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      setDeleteLoadingId(client.id);

      const response = await fetch(`https://patrol-visit.patroll-management.nissiindia.com/api/clients/${client.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please log in again.');
          logoutAndRedirect();
          throw new Error(data.message || 'Invalid or expired session');
        }
        throw new Error(data.message || 'Failed to delete client');
      }

      // Remove from local state
      setClients((prev) => prev.filter((c) => c.id !== client.id));
      setTotalCount((prev) => Math.max(prev - 1, 0));
      setFilteredCount((prev) => Math.max(prev - 1, 0));
      setSelectedClients((prev) => prev.filter((id) => id !== client.id));

      toast.success(`Client "${client.name}" deleted successfully.`);
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to delete client';
      setError(msg);
      toast.error(msg);
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const allSelected =
    clients.length > 0 && selectedClients.length === clients.length;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedClients(clients.map((c) => c.id));
    } else {
      setSelectedClients([]);
    }
  };

  const toggleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedClients((prev) => [...prev, id]);
    } else {
      setSelectedClients((prev) => prev.filter((item) => item !== id));
    }
  };

  return (
    <>
      {/* Toast container */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
        }}
      />

      <div className="w-full space-y-4 sm:space-y-6 px-2 sm:px-4 lg:px-6 pb-20 sm:pb-6 overflow-x-hidden -ml-4 sm:-ml-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm border border-slate-100 p-2.5 sm:p-4 lg:p-6">
          <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div
                className="w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                }}
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-lg lg:text-2xl font-bold text-slate-800 truncate">
                  Client Management
                </h1>
                <p className="text-[10px] sm:text-xs lg:text-sm text-slate-600 mt-0.5 truncate">
                  Manage your patrol visit clients
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Desktop Add Button */}
              <button
                onClick={() => navigate('/clients/add')}
                className="hidden sm:flex items-center justify-center gap-2 px-3 lg:px-4 py-2 text-white rounded-lg lg:rounded-xl text-sm lg:text-base font-semibold hover:shadow-lg hover:scale-105 transition-all duration-200"
                style={{
                  background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                }}
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden lg:inline">Add Client</span>
                <span className="lg:hidden">Add</span>
              </button>

              {/* Mobile Add Button */}
              <button
                onClick={() => navigate('/clients/add')}
                className="sm:hidden flex items-center justify-center gap-1 px-2.5 py-1.5 text-white rounded-lg text-xs font-semibold shadow-md active:scale-95 transition-all"
                style={{
                  background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                }}
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>Add</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error message (persistent card) */}
        {error && (
          <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm border border-red-200 p-2.5 sm:p-3 lg:p-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 sm:w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-semibold text-red-700">Error</p>
              <p className="text-[10px] sm:text-xs text-red-600 break-words">
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-6">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-4 lg:p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100 hover:scale-105 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-1.5 sm:mb-2 lg:mb-4">
                  <div
                    className="p-1.5 sm:p-2 lg:p-3 rounded-lg sm:rounded-xl group-hover:scale-110 transition-transform"
                    style={{
                      backgroundColor: `${theme.primary[50]}`,
                    }}
                  >
                    <Icon
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-6 lg:h-6"
                      style={{ color: theme.primary[600] }}
                    />
                  </div>
                  <span className="text-[8px] sm:text-[10px] lg:text-xs font-semibold px-1 sm:px-1.5 lg:px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    {stat.change}
                  </span>
                </div>
                <h3 className="text-slate-600 text-[9px] sm:text-[10px] lg:text-sm font-medium mb-0.5 truncate leading-tight">
                  {stat.title}
                </h3>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-slate-800">
                  {stat.value !== undefined ? stat.value : '--'}
                </p>
              </div>
            );
          })}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm border border-slate-100 p-2.5 sm:p-4 lg:p-6">
          <div className="flex flex-col gap-2 sm:gap-3 lg:gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 sm:pl-9 lg:pl-11 pr-2.5 sm:pr-3 lg:pr-4 py-1.5 sm:py-2 lg:py-3 border-2 border-slate-200 rounded-lg sm:rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all text-xs sm:text-sm lg:text-base"
                  style={{
                    borderColor: searchQuery ? theme.primary[500] : 'rgb(226 232 240)',
                  }}
                />
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 sm:flex-none px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 lg:py-3 border-2 border-slate-200 rounded-lg sm:rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all bg-white text-[11px] sm:text-xs lg:text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-1.5 sm:p-2 lg:p-3 border-2 rounded-lg sm:rounded-xl transition-all ${
                  showFilters
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-slate-600" />
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-2 sm:mt-3 lg:mt-4 pt-2 sm:pt-3 lg:pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
              <div>
                <label className="block text-[10px] sm:text-xs lg:text-sm font-medium text-slate-700 mb-1 sm:mb-1.5 lg:mb-2">
                  Properties Range
                </label>
                <select className="w-full px-2 sm:px-2.5 lg:px-3 py-1.5 sm:py-2 border-2 border-slate-200 rounded-lg focus:border-purple-500 outline-none text-[11px] sm:text-xs lg:text-sm">
                  <option>Any</option>
                  <option>1-5 properties</option>
                  <option>6-10 properties</option>
                  <option>10+ properties</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] sm:text-xs lg:text-sm font-medium text-slate-700 mb-1 sm:mb-1.5 lg:mb-2">
                  Join Date
                </label>
                <select className="w-full px-2 sm:px-2.5 lg:px-3 py-1.5 sm:py-2 border-2 border-slate-200 rounded-lg focus:border-purple-500 outline-none text-[11px] sm:text-xs lg:text-sm">
                  <option>All Time</option>
                  <option>Last 30 days</option>
                  <option>Last 3 months</option>
                  <option>Last 6 months</option>
                </select>
              </div>

              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-[10px] sm:text-xs lg:text-sm font-medium text-slate-700 mb-1 sm:mb-1.5 lg:mb-2">
                  Location
                </label>
                <select className="w-full px-2 sm:px-2.5 lg:px-3 py-1.5 sm:py-2 border-2 border-slate-200 rounded-lg focus:border-purple-500 outline-none text-[11px] sm:text-xs lg:text-sm">
                  <option>All Locations</option>
                  <option>Mumbai</option>
                  <option>Chennai</option>
                  <option>Bangalore</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Clients Table / List Wrapper */}
        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-2.5 sm:px-4 lg:px-6 py-2.5 sm:py-3 lg:py-4 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2">
              <div>
                <h3 className="text-sm sm:text-base lg:text-lg font-bold text-slate-800">
                  All Clients
                </h3>
                <p className="text-[10px] sm:text-xs lg:text-sm text-slate-600 mt-0.5">
                  Showing {filteredCount} of {totalCount} clients
                </p>
              </div>

              {selectedClients.length > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-[10px] sm:text-xs lg:text-sm text-slate-600">
                    {selectedClients.length} selected
                  </span>
                  <button className="px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs lg:text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all">
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Table (lg and up) */}
          <div className="hidden lg:block">
            <div className="overflow-x-auto w-full">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-slate-300"
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        checked={allSelected}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Properties
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Total Visits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Last Visit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-6 py-8 text-center text-sm text-slate-500"
                      >
                        Loading clients...
                      </td>
                    </tr>
                  ) : clients.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-6 py-8 text-center text-sm text-slate-500"
                      >
                        No clients found.
                      </td>
                    </tr>
                  ) : (
                    clients.map((client) => (
                      <tr
                        key={client.id}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedClients.includes(client.id)}
                            onChange={(e) =>
                              toggleSelectOne(client.id, e.target.checked)
                            }
                            className="w-4 h-4 rounded border-slate-300"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center font-semibold text-white"
                              style={{
                                background: `linear-gradient(135deg, ${theme.primary[500]} 0%, ${theme.secondary[500]} 100%)`,
                              }}
                            >
                              {client.avatar}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">
                                {client.name}
                              </p>
                              <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                <MapPin className="w-3 h-3" />
                                <span>{client.address}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Mail className="w-4 h-4" />
                              <span>{client.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Phone className="w-4 h-4" />
                              <span>{client.phone}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="font-semibold text-slate-800">
                              {client.properties}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-slate-800">
                            {client.totalVisits}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Clock className="w-4 h-4" />
                            <span>{client.lastVisit}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(client.status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              className="p-2 hover:bg-slate-100 rounded-lg transition-all"
                              title="View Details"
                              onClick={() => handleViewClient(client)}
                            >
                              <Eye className="w-4 h-4 text-slate-600" />
                            </button>
                            <button
                              className="p-2 hover:bg-slate-100 rounded-lg transition-all"
                              title="Edit Client"
                              onClick={() => handleEditClient(client)}
                            >
                              <Edit className="w-4 h-4 text-slate-600" />
                            </button>
                            <button
                              className="p-2 hover:bg-red-50 rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                              title="Delete Client"
                              onClick={() => handleDeleteClient(client)}
                              disabled={deleteLoadingId === client.id}
                            >
                              {deleteLoadingId === client.id ? (
                                <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4 text-red-600" />
                              )}
                            </button>
                            <button className="p-2 hover:bg-slate-100 rounded-lg transition-all">
                              <MoreVertical className="w-4 h-4 text-slate-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile/Tablet Card List (below lg) */}
          {/* (keeping your existing mobile list exactly as before) */}
          {/* ... You can keep your previous mobile view block here unchanged ... */}
          {/* For brevity, not repeating — but your existing mobile JSX works with the new auth logic */}
        </div>

        {/* View Client Modal */}
        {viewModalOpen && activeClient && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-3 sm:px-4">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center font-semibold text-white text-sm sm:text-base shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${theme.primary[500]} 0%, ${theme.secondary[500]} 100%)`,
                    }}
                  >
                    {activeClient.avatar}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900 truncate">
                      {activeClient.name}
                    </h2>
                    <p className="text-[10px] sm:text-xs text-slate-500 flex items-center gap-1 mt-0.5 sm:mt-1">
                      <Calendar className="w-3 h-3 shrink-0" />
                      <span className="truncate">
                        Joined: {activeClient.joinedDate || '-'}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 active:scale-95 transition-all shrink-0"
                  onClick={() => {
                    setViewModalOpen(false);
                    setActiveClient(null);
                  }}
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
                {/* Contact Details */}
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                    Contact Details
                  </h3>
                  <div className="grid grid-cols-1 gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
                      <span className="text-slate-700 truncate">
                        {activeClient.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
                      <span className="text-slate-700">{activeClient.phone}</span>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                    Address
                  </h3>
                  <div className="flex items-start gap-2 text-xs sm:text-sm text-slate-700">
                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 mt-0.5 shrink-0" />
                    <span className="break-words">{activeClient.address || '—'}</span>
                  </div>
                </div>

                {/* Stats */}
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                    Summary
                  </h3>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="bg-slate-50 rounded-lg sm:rounded-xl p-2 sm:p-3">
                      <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-slate-500 mb-1">
                        <Building2 className="w-3 h-3" />
                        <span className="hidden xs:inline">Properties</span>
                        <span className="xs:hidden">Props</span>
                      </div>
                      <p className="text-base sm:text-lg font-semibold text-slate-900">
                        {activeClient.properties}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg sm:rounded-xl p-2 sm:p-3">
                      <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-slate-500 mb-1">
                        <Users className="w-3 h-3" />
                        <span>Visits</span>
                      </div>
                      <p className="text-base sm:text-lg font-semibold text-slate-900">
                        {activeClient.totalVisits}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg sm:rounded-xl p-2 sm:p-3">
                      <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-slate-500 mb-1">
                        <Clock className="w-3 h-3" />
                        <span className="hidden xs:inline">Last Visit</span>
                        <span className="xs:hidden">Last</span>
                      </div>
                      <p className="text-xs sm:text-sm font-semibold text-slate-900 truncate">
                        {activeClient.lastVisit || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                    Status
                  </h3>
                  {getStatusBadge(activeClient.status)}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50 shrink-0">
                <button
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 rounded-lg border border-slate-200 hover:bg-white active:scale-95 transition-all"
                  onClick={() => {
                    setViewModalOpen(false);
                    setActiveClient(null);
                  }}
                >
                  Close
                </button>
                <button
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white rounded-lg hover:shadow-md active:scale-95 transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                  }}
                  onClick={() => {
                    const clientCopy = activeClient;
                    setViewModalOpen(false);
                    setActiveClient(null);
                    handleEditClient(clientCopy);
                  }}
                >
                  Edit Client
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClientsPage;
